/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { useEffect, useState } from "react";

import LOCAL_STORAGE_KEYS from "constants/STORAGE_KEYS";
import { getUserId } from "Models/User";
import { useInvitation } from "state/InvitationContext";
import { useUser } from "state/UserContext";

const FIVE_MINUTES = 1000 * 60 * 5;

const useCompanion = (attendingId?: string, opts?: { stale: true }) => {
  const SETTINGS = { stale: true, ...opts };

  const { userData, setUserData, getUserProfile, updateProfile } = useUser();
  const { attendees, updateAttendees } = useInvitation();
  const [companion, setCompanion] = useState(null);

  const getCompanion = async () => {
    let id = attendingId;
    if (!id || id === "new") return null;

    // get companion by id from invitationCompanions
    let companion = attendees?.find((user) => {
      if (user.hash_id === id) return user;
      if (user.id == id) return user;
      return false;
    });

    // Identity check must come first: a freshly-registered user can appear
    // inside `companions_details` with role "companion", and `userData` may
    // not be hydrated yet on a hard refresh — both cases used to mis-route
    // the request to `companion-data/<own hash_id>` and 403. Falling back to
    // the persisted localStorage id covers the userData race.
    const localId = localStorage.getItem(LOCAL_STORAGE_KEYS.id);
    const isMainUser =
      attendingId === getUserId(userData) || attendingId === localId;
    let role: "user" | "companion" = isMainUser
      ? "user"
      : companion?.role || "companion";

    id = getUserId(companion) || attendingId;

    let profile = await getUserProfile(id!, role);

    // if profile is stale, fetch it again
    if (
      !profile.lastFetched ||
      Date.now() - profile.lastFetched > FIVE_MINUTES
    ) {
      profile.stale = true;
      SETTINGS.stale = false;
    }

    if (
      profile.stale &&
      (SETTINGS.stale === false || profile.hasOwnProperty("blink_id") === false)
    ) {
      profile = await getUserProfile(getUserId(companion), role, true);
    }

    if (role === "user") {
      setUserData((prev) => ({
        ...prev,
        ...profile,
      }));
    }

    setCompanion(profile);
  };

  useEffect(() => {
    if (!attendingId) return;
    getCompanion();
  }, [attendingId]);

  const updateCompanion = (companion: any) => {
    // update companion in invitationCompanions
    const newCompanions = attendees.map((c) => {
      if (
        getUserId(c) === getUserId(companion) &&
        getUserId(c) !== undefined &&
        getUserId(c) !== null
      ) {
        return {
          ...c,
          ...companion,
          id: companion.id || c.id,
          hash_id: companion.hash_id || c.hash_id,
        };
      }

      return c;
    });

    updateAttendees(newCompanions);
    updateProfile(getUserId(companion), companion);

    if (companion.id === userData?.id) {
      setUserData((prev) => ({
        ...prev,
        ...companion,
      }));
    }

    return newCompanions;
  };

  // In `useInvitationCompanions` we fill the array with empty instances as placeholders, so we need to hydrate then with real data.
  const updateExistingCompanionOrCreateNewCompanion = (companion: any) => {
    const newCompanions = [...attendees];

    let targetIndex = newCompanions.findIndex(
      (c) => getUserId(c) === getUserId(companion),
    );

    // fill empty companions if you can't find the companion
    if (targetIndex === -1) {
      targetIndex = newCompanions.findIndex(
        (c) => c.hasOwnProperty("id") === false,
      );

      newCompanions[targetIndex] = companion;
      updateAttendees(newCompanions);
    } else {
      // to update the profile.
      updateCompanion(companion);
    }
  };

  return [
    companion,
    updateCompanion,
    updateExistingCompanionOrCreateNewCompanion,
  ] as const;
};

export default useCompanion;
